import React, { useEffect, useMemo, useState } from 'react';
import { Card } from '../ui/card';
import { useMyTeams } from '../teams/useMyTeams';
import TeamActionRow from './TeamActionRow';
import { useAuth } from '../../auth/AuthContext';
import { db } from '../../firebase-client';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getPendingMemberDisplayName } from '../../utils/waitlist';
import { useOnlineRefreshSignal } from '../../utils/onlineStatus';
import { getBespokeModuleSupport } from '../teams/utils/teamMetadata';
const JOIN_TERMINAL_STATUSES = new Set(['approved', 'rejected', 'declined', 'denied', 'cancelled', 'withdrawn', 'paid', 'payment']);
const isAdminRole = role => {
  const normalized = String(role || '').trim().toLowerCase();
  return normalized === 'admin' || normalized === 'owner';
};
const isPendingJoinStatus = status => {
  const normalized = String(status || '').trim().toLowerCase();
  if (!normalized) return true; // legacy docs may omit status
  if (normalized === 'invited') return false;
  return !JOIN_TERMINAL_STATUSES.has(normalized);
};
const isPendingRoleRequestStatus = status => {
  const normalized = String(status || '').trim().toLowerCase();
  return !normalized || normalized === 'requested' || normalized === 'pending';
};
const isPendingMembershipRequestStatus = status => {
  const normalized = String(status || '').trim().toLowerCase();
  return !normalized || normalized === 'requested' || normalized === 'pending';
};
const toMillis = value => {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number') return value;
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? 0 : parsed;
};
const chunk = (items, size = 10) => {
  const output = [];
  for (let i = 0; i < items.length; i += size) output.push(items.slice(i, i + size));
  return output;
};
const fetchUserProfiles = async userIds => {
  const uniqueIds = Array.from(new Set((Array.isArray(userIds) ? userIds : []).map(id => String(id || '').trim()).filter(Boolean)));
  if (uniqueIds.length === 0) return new Map();
  const usersById = new Map();
  await Promise.all(chunk(uniqueIds, 10).map(async ids => {
    const snap = await getDocs(query(collection(db, 'users'), where('__name__', 'in', ids)));
    snap.forEach(docSnap => {
      usersById.set(docSnap.id, {
        id: docSnap.id,
        ...(docSnap.data() || {})
      });
    });
  }));
  return usersById;
};
export default function TeamActions() {
  const {
    teams,
    loading
  } = useMyTeams();
  const {
    user
  } = useAuth();
  const [actions, setActions] = useState([]);
  const [resolvedAdminTeams, setResolvedAdminTeams] = useState([]);
  const [actionsLoading, setActionsLoading] = useState(false);
  const onlineRefreshSignal = useOnlineRefreshSignal();
  const adminTeamsFromProfile = useMemo(() => teams.filter(team => isAdminRole(team.role) || (user?.uid && Array.isArray(team.admins) && team.admins.includes(user.uid))), [teams, user?.uid]);
  useEffect(() => {
    if (loading) return undefined;
    let cancelled = false;
    const resolveAdminTeams = async () => {
      const byId = new Map(adminTeamsFromProfile.map(team => [team.id, team]));
      if (user?.uid) {
        try {
          const fallbackQuery = query(collection(db, 'teams'), where('admins', 'array-contains', user.uid));
          const fallbackSnap = await getDocs(fallbackQuery);
          fallbackSnap.forEach(docSnap => {
            byId.set(docSnap.id, {
              ...(byId.get(docSnap.id) || {}),
              id: docSnap.id,
              ...(docSnap.data() || {}),
              role: 'admin'
            });
          });
        } catch (err) {}
      }
      if (!cancelled) {
        setResolvedAdminTeams(Array.from(byId.values()));
      }
    };
    resolveAdminTeams();
    return () => {
      cancelled = true;
    };
  }, [adminTeamsFromProfile, loading, onlineRefreshSignal, user?.uid]);
  useEffect(() => {
    if (loading) return undefined;
    const adminTeams = resolvedAdminTeams;
    if (adminTeams.length === 0) {
      setActions([]);
      setActionsLoading(false);
      return undefined;
    }
    let cancelled = false;
    const fetchActions = async ({
      showLoader = false
    } = {}) => {
      if (showLoader && !cancelled) setActionsLoading(true);
      try {
        const teamActionBatches = await Promise.all(adminTeams.map(async team => {
          const teamActions = [];
          const bespokeSupport = getBespokeModuleSupport(team?.teamMetadata || {});
          const learnToRowEnabled = bespokeSupport.rowing && team?.modules?.rowing?.learnToRow?.enabled === true;
          const learnToSailEnabled = bespokeSupport.sailing && team?.modules?.sailing?.learnToSail?.enabled === true;
          const [pendingResult, legacyJoinResult, membershipResult, learnToRowApprovalResult, learnToSailApprovalResult, eventsResult] = await Promise.allSettled([
            getDocs(collection(db, 'teams', team.id, 'pendingMembers')),
            getDocs(collection(db, 'teams', team.id, 'joinRequests')),
            getDocs(collection(db, 'teams', team.id, 'membershipRequests')),
            learnToRowEnabled ? getDocs(query(collection(db, 'teams', team.id, 'learnToRowWaitlist'), where('status', '==', 'awaiting_approval'))) : Promise.resolve(null),
            learnToSailEnabled ? getDocs(query(collection(db, 'teams', team.id, 'learnToSailWaitlist'), where('status', '==', 'awaiting_approval'))) : Promise.resolve(null),
            getDocs(collection(db, 'teams', team.id, 'events')),
          ]);
          if (pendingResult.status === 'fulfilled') {
            pendingResult.value.forEach(docSnap => {
              const data = docSnap.data() || {};
              if (!isPendingJoinStatus(data.status)) return;
              teamActions.push({
                id: docSnap.id,
                teamId: team.id,
                teamName: team.name,
                type: 'pendingJoinRequest',
                data,
                sortTs: toMillis(data.updatedAt || data.createdAt)
              });
            });
          } else {}
          if (legacyJoinResult.status === 'fulfilled') {
            legacyJoinResult.value.forEach(docSnap => {
              const data = docSnap.data() || {};
              if (!isPendingJoinStatus(data.status)) return;
              teamActions.push({
                id: `legacy:${docSnap.id}`,
                teamId: team.id,
                teamName: team.name,
                type: 'pendingJoinRequest',
                data,
                sortTs: toMillis(data.updatedAt || data.createdAt)
              });
            });
          } else if (String(legacyJoinResult.reason?.code || '') !== 'permission-denied') {}
          if (membershipResult.status === 'fulfilled') {
            membershipResult.value.forEach(docSnap => {
              const data = docSnap.data() || {};
              if (!isPendingMembershipRequestStatus(data.status)) return;
              teamActions.push({
                id: `membership:${docSnap.id}`,
                teamId: team.id,
                teamName: team.name,
                type: 'membershipRequest',
                data: {
                  requestedBy: data.requestedBy || docSnap.id,
                  requestedByName: data.requestedByName || data.memberName || data.requestedBy || 'Member',
                  message: data.message || ''
                },
                sortTs: toMillis(data.updatedAt || data.createdAt)
              });
            });
          } else if (String(membershipResult.reason?.code || '') !== 'permission-denied') {}
          if (learnToRowApprovalResult.status === 'fulfilled' && learnToRowApprovalResult.value) {
            const approvalDocs = learnToRowApprovalResult.value.docs.map(docSnap => ({
              id: docSnap.id,
              ...(docSnap.data() || {})
            }));
            const usersById = await fetchUserProfiles(approvalDocs.flatMap(entry => [entry.id, entry.userId, entry.payerUserId]));
            approvalDocs.forEach(entry => {
              const merged = {
                ...entry,
                ...(usersById.get(entry.userId) || usersById.get(entry.id) || {})
              };
              teamActions.push({
                id: `learnToRow:${entry.id}`,
                teamId: team.id,
                teamName: team.name,
                type: 'learnToRowApproval',
                data: {
                  ...merged,
                  displayName: getPendingMemberDisplayName(merged)
                },
                sortTs: toMillis(entry.updatedAt || entry.approvedAt || entry.createdAt)
              });
            });
          } else if (String(learnToRowApprovalResult.reason?.code || '') !== 'permission-denied') {}
          if (learnToSailApprovalResult.status === 'fulfilled' && learnToSailApprovalResult.value) {
            const approvalDocs = learnToSailApprovalResult.value.docs.map(docSnap => ({
              id: docSnap.id,
              ...(docSnap.data() || {})
            }));
            const usersById = await fetchUserProfiles(approvalDocs.flatMap(entry => [entry.id, entry.userId, entry.payerUserId]));
            approvalDocs.forEach(entry => {
              const merged = {
                ...entry,
                ...(usersById.get(entry.userId) || usersById.get(entry.id) || {})
              };
              teamActions.push({
                id: `learnToSail:${entry.id}`,
                teamId: team.id,
                teamName: team.name,
                type: 'learnToSailApproval',
                data: {
                  ...merged,
                  displayName: getPendingMemberDisplayName(merged)
                },
                sortTs: toMillis(entry.updatedAt || entry.approvedAt || entry.createdAt)
              });
            });
          } else if (String(learnToSailApprovalResult.reason?.code || '') !== 'permission-denied') {}
          if (eventsResult.status !== 'fulfilled') {
            return teamActions;
          }
          await Promise.all(eventsResult.value.docs.map(async evt => {
            const eventData = evt.data() || {};
            const [refundResult, roleResult] = await Promise.allSettled([getDocs(collection(db, 'teams', team.id, 'events', evt.id, 'refundRequests')), getDocs(collection(db, 'teams', team.id, 'events', evt.id, 'roleRequests'))]);
            if (refundResult.status === 'fulfilled') {
              refundResult.value.forEach(r => {
                const req = r.data() || {};
                if (req.status !== 'requested') return;
                teamActions.push({
                  id: `${evt.id}:${r.id}`,
                  teamId: team.id,
                  teamName: team.name,
                  type: 'refundRequest',
                  data: {
                    eventId: evt.id,
                    ticketId: req.ticketId,
                    userId: req.userId || r.id,
                    requestedAt: req.createdAt
                  },
                  sortTs: toMillis(req.updatedAt || req.createdAt)
                });
              });
            } else {}
            if (roleResult.status === 'fulfilled') {
              roleResult.value.forEach(r => {
                const req = r.data() || {};
                if (!isPendingRoleRequestStatus(req.status)) return;
                teamActions.push({
                  id: `role:${evt.id}:${r.id}`,
                  teamId: team.id,
                  teamName: team.name,
                  type: 'roleRequest',
                  data: {
                    eventId: evt.id,
                    eventName: eventData.name || 'Event',
                    roleId: req.roleId || '',
                    roleName: req.roleName || 'Role',
                    slotId: req.slotId || '',
                    requestedBy: req.requestedBy || '',
                    requestedByName: req.requestedByName || req.requestedBy || 'Member'
                  },
                  sortTs: toMillis(req.updatedAt || req.createdAt)
                });
              });
            } else {}
          }));
          return teamActions;
        }));
        if (!cancelled) {
          const allActions = teamActionBatches.flat();
          setActions(allActions.sort((a, b) => {
            const delta = (b.sortTs || 0) - (a.sortTs || 0);
            if (delta !== 0) return delta;
            return String(a.id).localeCompare(String(b.id));
          }));
        }
      } catch (err) {
        // Keep the last successful actions during a transient refresh failure.
      } finally {
        if (showLoader && !cancelled) setActionsLoading(false);
      }
    };
    fetchActions({
      showLoader: true
    });
    const intervalId = window.setInterval(() => {
      fetchActions();
    }, 30000);
    const onFocus = () => {
      fetchActions();
    };
    window.addEventListener('focus', onFocus);
    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
    };
  }, [loading, onlineRefreshSignal, resolvedAdminTeams]);
  const adminTeams = resolvedAdminTeams;
  if (loading || adminTeams.length === 0) return null;
  return <Card title="Team Admin Actions" className="dashboard-card">
      {actionsLoading ? <p style={{
      color: 'var(--clr-text-muted)'
    }}>Loading team actions…</p> : actions.length === 0 ? <p style={{
      color: 'var(--clr-text-muted)'
    }}>No team actions to take.</p> : actions.map(action => <TeamActionRow key={`${action.teamId}-${action.id}`} action={action} />)}
    </Card>;
}
